// usePdfGenerator.ts
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_API_URL}/pdf-generator`;

const singlePageStyle = `
  <style>
    @font-face {
      font-family: 'NotoSansDevanagari';
      src: url('fonts/NotoSansDevanagari-Regular.ttf');
    }
    html, body { margin:0; padding:0; font-family:'NotoSansDevanagari', Arial, sans-serif !important; box-sizing:border-box; font-size:13px; }
    @page { size: A4; margin: 0; }
    .pdf-content { width:100%; box-sizing:border-box; }
  </style>
`;

/** Extract filename from `Content-Disposition` header if present */
function getFilenameFromContentDisposition(cd?: string, fallback = 'document.pdf') {
  if (!cd) return fallback;
  // examples: attachment; filename="file.pdf"; filename*=UTF-8''file.pdf
  const filenameStar = /filename\*\=([^']*)''([^;]+)/i.exec(cd);
  if (filenameStar && filenameStar[2]) return decodeURIComponent(filenameStar[2]);

  const filename = /filename=\"?([^\";]+)\"?/i.exec(cd);
  if (filename && filename[1]) return filename[1];

  return fallback;
}

const generatePdf = async (htmlContent: string, requestedFileName: string) => {
  // Always wrap with styles so iText uses A4/zero margins from @page
  const htmlToSend = singlePageStyle + `<div class="pdf-content">${htmlContent}</div>`;

  const response = await axios.post(
    `${API_URL}/generate`,
    { htmlContent: htmlToSend, fileName: requestedFileName },
    {
      headers: { 'Content-Type': 'application/json' },
      responseType: 'blob', // IMPORTANT: expect PDF bytes
    }
  );

  // Ensure we actually got a PDF
  const ct = response.headers['content-type'] || '';
  if (!ct.includes('application/pdf')) {
    // Read server text error (backend sends text/plain on errors)
    const text = await new Response(response.data).text();
    throw new Error(text || 'Failed to generate PDF');
  }

  // Prefer server-provided filename (Content-Disposition), else use requested
  const serverName = getFilenameFromContentDisposition(response.headers['content-disposition'], requestedFileName);

  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = serverName || requestedFileName || 'document.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);

  return { fileName: a.download };
};

export function usePdfGenerator() {
  const generatePdfMutation = useMutation({
    mutationFn: ({ htmlContent, fileName }: { htmlContent: string; fileName: string }) =>
      generatePdf(htmlContent, fileName),
    onSuccess: ({ fileName }) => {
      // Use a toast/snackbar if you have one
      // eslint-disable-next-line no-alert
      alert(`PDF generated: ${fileName}`);
    },
    onError: (err: any) => {
      // Try to surface server text message if any
      const msg = err?.message || 'Failed to generate PDF';
      // eslint-disable-next-line no-alert
      alert(msg);
    },
  });

  return {
    generatePdf: generatePdfMutation.mutate,
    isGenerating: generatePdfMutation.isLoading,
    error: generatePdfMutation.error,
  };
}
